import { useState, useRef } from "react";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { toast } from "../../../lib/toast";
import { Button } from "../../../components/ui/button";
import { Skeleton } from "../../../components/ui/skeleton";
import { CharLimitIndicator, useCharLimitHandlers } from "../../../components/CharLimitIndicator";
import { LIMITS } from "../../../lib/constants";
import type { Topico } from "../../../services/topico.service";
import type { ItemAta } from "../../../services/itemAta.service";
import type { Desenvolvimento } from "../../../services/desenvolvimento.service";

interface TopicSectionProps {
  topico: Topico;
  items: ItemAta[];
  desenvolvimentos: Desenvolvimento[];
  meetingNumber: number;
  lastMeetingNumber: number;
  meetingDate?: string;
  canEdit?: boolean;
  onAddItem: (topicoId: string, input: { descricao: string; entidade?: string; prazo?: string }) => Promise<void>;
  onEditItem: (item: ItemAta) => void;
  onDeleteItem: (itemId: string) => void;
  onToggleItemStatus: (itemId: string, concluido: boolean) => void;
  onAddDesenvolvimento: (itemId: string, texto: string) => Promise<void>;
  onUpdateDesenvolvimento: (desenvolvimentoId: string, texto: string) => void;
  onDeleteDesenvolvimento: (desenvolvimentoId: string) => void;
  onExportTopicHistory?: (topicoId: string) => void;
  onDeleteTopic?: (topicoId: string) => void;
}

export function TopicSection({
  topico,
  items,
  desenvolvimentos,
  meetingNumber,
  lastMeetingNumber,
  meetingDate,
  canEdit = true,
  onAddItem,
  onEditItem,
  onDeleteItem,
  onToggleItemStatus,
  onAddDesenvolvimento,
  onUpdateDesenvolvimento: _onUpdateDesenvolvimento,
  onDeleteDesenvolvimento: _onDeleteDesenvolvimento,
  onExportTopicHistory,
  onDeleteTopic,
}: TopicSectionProps) {
  const { i18n, t } = useTranslation('common');
  const [addingItem, setAddingItem] = useState(false);
  const [newItemDesc, setNewItemDesc] = useState("");
  const [newItemEntity, setNewItemEntity] = useState("");
  const [newItemDeadline, setNewItemDeadline] = useState("");
  const [addingDevToItem, setAddingDevToItem] = useState<string | null>(null);
  const [newDevText, setNewDevText] = useState("");
  const [savingDevToItem, setSavingDevToItem] = useState<string | null>(null);

  const newItemDescHandlers = useCharLimitHandlers(LIMITS.ITEM_DESCRIPTION_MAX, setNewItemDesc);
  const newItemEntityHandlers = useCharLimitHandlers(LIMITS.ITEM_ENTITY_MAX, setNewItemEntity);
  const newDevHandlers = useCharLimitHandlers(LIMITS.DEVELOPMENT_TEXT_MAX, setNewDevText);

  const newItemDescRef = useRef<HTMLInputElement>(null);

  const topicItems = items
    .filter((item) => item.topico_id === topico.id)
    .sort((a, b) => {
      const na = a.numero_item || '';
      const nb = b.numero_item || '';
      const partsA = na.split('.').map(p => parseInt(p.replace(/\D/g, ''), 10) || 0);
      const partsB = nb.split('.').map(p => parseInt(p.replace(/\D/g, ''), 10) || 0);
      for (let i = 0; i < Math.max(partsA.length, partsB.length); i++) {
        const diff = (partsA[i] || 0) - (partsB[i] || 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });


  const getUniqueEntities = () => {
    const entities = items
      .map((item) => item.entidade)
      .filter((e) => e && e.trim());
    return [...new Set(entities)].sort();
  };

  const handleAddItem = async () => {
    if (!newItemDesc.trim()) {
      toast.error("Descrição do item é obrigatória. Descreva o que precisa ser feito.");
      return;
    }
    await onAddItem(topico.id, {
      descricao: newItemDesc.trim(),
      entidade: newItemEntity.trim() || undefined,
      prazo: newItemDeadline || undefined,
    });
    setNewItemDesc("");
    setNewItemEntity("");
    setNewItemDeadline("");
    setAddingItem(false);
  };

  const handleAddDev = async (itemId: string) => {
    if (!newDevText.trim()) return;
    const texto = newDevText.trim();
    setNewDevText("");
    setAddingDevToItem(null);
    setSavingDevToItem(itemId);
    try {
      await onAddDesenvolvimento(itemId, texto);
    } finally {
      setSavingDevToItem(null);
    }
  };








  const getItemDesenvolvimentos = (itemId: string): Desenvolvimento[] => {
    return desenvolvimentos
      .filter((d) => d.item_id === itemId)
      .sort((a, b) => a.ata_numero - b.ata_numero);
  };

  return (
    <section
      id={`topic-${topico.id}`}
      className="mb-12 scroll-mt-24"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold" style={{ color: "#013EFB" }}>
          {topico.titulo}
        </h2>
        <div className="flex items-center gap-4">
          {onExportTopicHistory && (
            <button
              onClick={() => onExportTopicHistory(topico.id)}
              className="text-sm hover:opacity-80 transition-opacity"
              style={{ color: "#013EFB" }}
            >
              + Histórico do tópico
            </button>
          )}
          {canEdit && onDeleteTopic && (
            <button
              onClick={() => onDeleteTopic(topico.id)}
              className="flex items-center gap-1 text-sm text-destructive hover:opacity-80 transition-opacity"
              title="Excluir tópico"
            >
              <Trash2 className="h-4 w-4" />
              Excluir tópico
            </button>
          )}
        </div>
      </div>

      {}
      <div className="overflow-x-auto bg-card rounded-md border border-border">
        <table className="w-full border-collapse" style={{ minWidth: "600px" }}>
          <thead>
            <tr className="bg-muted/40">
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground border-b border-border" style={{ width: "60px" }}>
                ITEM
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground border-b border-border" style={{ width: "80px" }}>
                Nº ATA
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground border-b border-border" style={{ width: "120px", whiteSpace: "nowrap" }}>
                DATA
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground border-b border-border">
                DESCRIÇÃO
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground border-b border-border" style={{ width: "150px" }}>
                STATUS
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground border-b border-border" style={{ width: "120px", whiteSpace: "nowrap" }}>
                PRAZO
              </th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground border-b border-border" style={{ width: "100px" }}>
                AÇÃO
              </th>
            </tr>
          </thead>
          <tbody>
            {topicItems.length === 0 && !addingItem && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                  Nenhum item neste tópico ainda.
                </td>
              </tr>
            )}
            {topicItems.map((item, itemIdx) => {
              const itemDevs = getItemDesenvolvimentos(item.id);
              const isAdding = addingDevToItem === item.id;
              const itemNumber = item.numero_item || (itemIdx + 1);

              return (
                <tr
                  key={item.id}
                  className="hover:bg-muted/50 transition-colors relative border-b border-border"
                  style={{
                    opacity: item.concluido ? 0.5 : 1,
                  }}
                >
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{itemNumber}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="text-sm text-foreground">
                      {item.ata_origem_numero ? `#${item.ata_origem_numero}` : `#${meetingNumber}`}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <span className="text-sm text-foreground">
                      {item.ata_origem_data ? (
                        (() => {
                          try {
                            const dateStr = item.ata_origem_data.includes('T')
                              ? item.ata_origem_data
                              : item.ata_origem_data + 'T00:00:00';
                            return format(new Date(dateStr), "dd/MM/yyyy");
                          } catch {
                            return item.ata_origem_data;
                          }
                        })()
                      ) : (
                        <span className="text-muted-foreground text-xs">-</span>
                      )}
                    </span>
                  </td>
                  <td className="px-4 py-3 align-top">
                    <p
                      className="text-sm text-foreground"
                      style={{
                        textDecoration: item.concluido ? "line-through" : "none",
                      }}
                    >
                      {item.descricao}
                    </p>
                    {item.entidade && (
                      <div className="mt-1 text-xs text-muted-foreground">
                        {item.entidade}
                      </div>
                    )}
                    {itemDevs.length > 0 && (
                      <div className="mt-2 space-y-1 text-muted-foreground">
                        {itemDevs.map((dev) => {
                          const ataNumero = dev.ata_numero ?? 0;
                          const isNew = ataNumero === meetingNumber;
                          return (
                            <div key={dev.id} className="whitespace-pre-line">
                              <strong>A{ataNumero || '-'}:</strong> {dev.texto}
                              {isNew && (
                                <span className="ml-2 px-1.5 py-0.5 text-xs font-bold rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                  NOVO
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {savingDevToItem === item.id && (
                      <div className="mt-2">
                        <Skeleton className="h-5 w-full" />
                      </div>
                    )}
                    {isAdding && canEdit && (
                      <div className="mt-2 flex flex-col gap-2">
                        <textarea
                          value={newDevText}
                          onChange={newDevHandlers.onChange}
                          onPaste={newDevHandlers.onPaste}
                          maxLength={LIMITS.DEVELOPMENT_TEXT_MAX}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              if (e.ctrlKey) {
                                // Ctrl+Enter: inserir nova linha na posição do cursor
                                e.preventDefault();
                                const target = e.target as HTMLTextAreaElement;
                                const start = target.selectionStart;
                                const end = target.selectionEnd;
                                const newValue = newDevText.substring(0, start) + "\n" + newDevText.substring(end);
                                setNewDevText(newValue);
                                // Mover cursor para depois da nova linha
                                setTimeout(() => {
                                  target.selectionStart = target.selectionEnd = start + 1;
                                }, 0);
                                return;
                              }
                              // Enter simples: salva
                              e.preventDefault();
                              handleAddDev(item.id);
                            } else if (e.key === "Escape") {
                              setAddingDevToItem(null);
                              setNewDevText("");
                            }
                          }}
                          placeholder="Digite o desenvolvimento aqui...&#10;&#10;Use Ctrl+Enter para criar nova linha."
                          autoFocus
                          rows={8}
                          className="w-full px-3 py-2 border rounded text-sm resize-y min-h-[180px] bg-background text-foreground"
                          style={{ borderColor: "#013EFB" }}
                        />
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground">
                            💡 <kbd className="px-1 py-0.5 bg-muted rounded font-semibold border border-border">Enter</kbd> adicionar • <kbd className="px-1 py-0.5 bg-muted rounded font-semibold border border-border">Ctrl+Enter</kbd> nova linha • <kbd className="px-1 py-0.5 bg-muted rounded font-semibold border border-border">Esc</kbd> cancelar
                          </span>
                          <CharLimitIndicator current={newDevText.length} max={LIMITS.DEVELOPMENT_TEXT_MAX} />
                          <Button
                            onClick={() => handleAddDev(item.id)}
                            disabled={!newDevText.trim()}
                            size="sm"
                            className="whitespace-nowrap"
                          >
                            Adicionar
                          </Button>
                        </div>
                      </div>
                    )}
                    {!isAdding && canEdit && (
                      <button
                        onClick={() => setAddingDevToItem(item.id)}
                        className="mt-2 text-xs hover:opacity-80 transition-opacity"
                        style={{ color: "#013EFB" }}
                      >
                        + Adicionar desenvolvimento
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {canEdit ? (
                      <button
                        onClick={() => onToggleItemStatus(item.id, !item.concluido)}
                        className="inline-flex items-center gap-2 group cursor-pointer select-none"
                        title={item.concluido ? "Clique para marcar como Em andamento" : "Clique para marcar como Finalizado"}
                      >
                        <span className={`relative flex h-2.5 w-2.5 shrink-0 rounded-full transition-colors duration-300 ${
                          item.concluido ? "bg-green-500" : "bg-amber-400"
                        }`}>
                          {!item.concluido && (
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
                          )}
                        </span>
                        <span className={`text-xs font-medium transition-colors duration-200 ${
                          item.concluido
                            ? "text-green-700 group-hover:text-green-600"
                            : "text-amber-700 group-hover:text-amber-600"
                        }`}>
                          {item.concluido ? "Finalizado" : "Em andamento"}
                        </span>
                      </button>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${
                          item.concluido ? "bg-green-500" : "bg-amber-400"
                        }`} />
                        <span className={`text-xs font-medium ${
                          item.concluido ? "text-green-700" : "text-amber-700"
                        }`}>
                          {item.concluido ? "Finalizado" : "Em andamento"}
                        </span>
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    {item.prazo ? (() => {
                      const prazoDate = new Date(item.prazo + 'T00:00:00');
                      if (Number.isNaN(prazoDate.getTime())) {
                        return <span className="text-xs text-muted-foreground">{t('invalid_deadline')}</span>;
                      }
                      const hoje = new Date();
                      hoje.setHours(0,0,0,0);
                      const vencido = !item.concluido && prazoDate < hoje;
                      return (
                        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${
                          vencido ? "text-red-600" : "text-muted-foreground"
                        }`}>
                          {vencido && <span className="h-1.5 w-1.5 rounded-full bg-red-500 shrink-0" />}
                          {format(prazoDate, "dd/MM/yyyy")}
                        </span>
                      );
                    })() : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 align-top">
                    <div className="flex items-center gap-1">
                      {canEdit && (
                        <>
                          <Button
                            onClick={() => onEditItem(item)}
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2"
                            title="Editar item"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => onDeleteItem(item.id)}
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Excluir item"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {addingItem && canEdit && (
              <tr className="border-b border-border bg-muted/30">
                <td className="px-4 py-3"></td>
                <td className="px-4 py-3">
                  <input
                    type="number"
                    value={meetingNumber}
                    readOnly
                    className="w-16 px-2 py-1 border border-border rounded text-center text-sm bg-muted/30 text-foreground"
                  />
                </td>
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {meetingDate
                    ? format(new Date(meetingDate + "T00:00:00"), "dd/MM/yyyy")
                    : new Date().toLocaleDateString(i18n.language)}
                </td>
                <td className="px-4 py-3">
                  <input
                    ref={newItemDescRef}
                    type="text"
                    value={newItemDesc}
                    onChange={newItemDescHandlers.onChange}
                    onPaste={newItemDescHandlers.onPaste}
                    maxLength={LIMITS.ITEM_DESCRIPTION_MAX}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && newItemDesc.trim()) {
                        e.preventDefault();
                        handleAddItem();
                      } else if (e.key === "Tab" && !e.shiftKey) {
                        e.preventDefault();
                        document.getElementById(`new-owner-${topico.id}`)?.focus();
                      } else if (e.key === "Escape") {
                        setAddingItem(false);
                        setNewItemDesc("");
                        setNewItemEntity("");
                        setNewItemDeadline("");
                      }
                    }}
                    placeholder="Título do assunto"
                    autoFocus
                    className="w-full px-3 py-2 border rounded mb-2 bg-background text-foreground"
                    style={{ borderColor: "#013EFB" }}
                  />
                  <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">Responsável</label>
                      <input
                        id={`new-owner-${topico.id}`}
                        type="text"
                        list={`entity-list-${topico.id}`}
                        value={newItemEntity}
                        onChange={newItemEntityHandlers.onChange}
                        onPaste={newItemEntityHandlers.onPaste}
                        maxLength={LIMITS.ITEM_ENTITY_MAX}
                        onKeyDown={(e) => {
                          if (e.key === "Tab" && !e.shiftKey) {
                            e.preventDefault();
                            document.getElementById(`new-deadline-${topico.id}`)?.focus();
                          }
                        }}
                        placeholder="Digite ou selecione"
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
                      />
                      <datalist id={`entity-list-${topico.id}`}>
                        {getUniqueEntities().map((entity) => (
                          <option key={entity} value={entity} />
                        ))}
                      </datalist>
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-medium text-muted-foreground mb-1">
                        Prazo
                      </label>
                      <input
                        id={`new-deadline-${topico.id}`}
                        type="date"
                        value={newItemDeadline}
                        onChange={(e) => setNewItemDeadline(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddItem();
                          }
                        }}
                        className="w-full px-3 py-2 border border-border rounded bg-background text-foreground"
                      />
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3 align-top">
                  <span className="inline-flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5 shrink-0 rounded-full bg-amber-400">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-60" />
                    </span>
                    <span className="text-xs font-medium text-amber-700">Em andamento</span>
                  </span>
                </td>
                <td className="px-4 py-3 align-top">
                  {newItemDeadline ? (
                    <span className="text-xs text-muted-foreground">
                      {format(new Date(newItemDeadline + 'T00:00:00'), "dd/MM/yyyy")}
                    </span>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={handleAddItem}
                      disabled={!newItemDesc.trim()}
                      variant="default"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      Salvar
                    </Button>
                    <Button
                      onClick={() => {
                        setAddingItem(false);
                        setNewItemDesc("");
                        setNewItemEntity("");
                        setNewItemDeadline("");
                      }}
                      variant="outline"
                      size="sm"
                      className="whitespace-nowrap"
                    >
                      Cancelar
                    </Button>
                  </div>
                </td>
              </tr>
            )}
            {!addingItem && canEdit && (
              <tr>
                <td colSpan={7} className="px-4 py-3">
                  <button
                    onClick={() => {
                      setAddingItem(true);
                      setNewItemDeadline(""); // campo sempre vazio
                      setTimeout(() => newItemDescRef.current?.focus(), 100);
                    }}
                    className="w-full py-3 border border-dashed border-border rounded-md hover:bg-muted/50 transition-colors flex items-center justify-center gap-2 text-muted-foreground"
                  >
                    <Plus size={18} />
                    Novo Item
                  </button>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

